import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bookmark, Trash2, MessageSquare, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

export default function FavoritesPage() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadFavorites(); }, []);

  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${BACKEND_URL}/api/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(resp.data);
    } catch {} finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/favorites/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(prev => prev.filter(f => f.id !== id));
      toast.success('Usunięto z ulubionych');
    } catch { toast.error('Błąd usuwania'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50/30 to-orange-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <div className="border-b border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="gap-2 dark:text-slate-200">
              <ArrowLeft className="w-4 h-4" />
              Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <h1 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold flex items-center gap-2">
              <Bookmark className="w-5 h-5" />
              Ulubione odpowiedzi ({favorites.length})
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <Clock className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-3" />
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-16">
            <Bookmark className="w-16 h-16 text-slate-200 dark:text-slate-700 mx-auto mb-4" />
            <h3 className="text-lg text-slate-600 dark:text-slate-400 mb-2">Brak ulubionych</h3>
            <p className="text-sm text-slate-400 dark:text-slate-500">Kliknij ikonę zakładki przy odpowiedzi KAI aby zapisać</p>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map((fav) => (
              <div
                key={fav.id}
                data-testid={`fav-item-${fav.id}`}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow"
              >
                {fav.question && (
                  <div className="flex items-start gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-medium text-primary-900 dark:text-slate-200">{fav.question}</p>
                  </div>
                )}
                <div className="pl-6 text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {fav.answer?.substring(0, 500)}{fav.answer?.length > 500 ? '...' : ''}
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <span className="text-xs text-slate-400">
                    {new Date(fav.created_at).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(fav.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    data-testid={`delete-fav-${fav.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
